import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://dsfcartech.be';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset error status when user starts typing
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validatie
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Vul alle verplichte velden in.');
      return;
    }

    // Email validatie
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Vul een geldig e-mailadres in.');
      return;
    }

    // Telefoon validatie (basis)
    if (formData.phone.length < 9) {
      setSubmitStatus('error');
      setErrorMessage('Vul een geldig telefoonnummer in.');
      return;
    }

    // Textarea limiet check
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setErrorMessage('Het bericht mag maximaal 500 karakters bevatten.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Verstuur naar Formspree endpoint
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service || 'Niet gespecificeerd');
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://formspree.io/f/mjggrovy', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset formulier
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
        // Scroll naar boven om success bericht te tonen
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Formulier versturen mislukt');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Er ging iets mis bij het versturen. Probeer het opnieuw of bel ons direct op 0489 17 05 92.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    {
      question: 'Hoe lang duurt een CarPlay installatie?',
      answer: 'De meeste installaties worden binnen 1 dag afgerond. Voor complexere upgrades zoals sterrenhemel of volledige interieur verlichting kan het 1-2 dagen duren. We plannen altijd een realistische tijdsinschatting tijdens de offerte.'
    },
    {
      question: 'Krijg ik garantie op de installatie?',
      answer: 'Ja! We bieden garantie op al onze installaties en gebruiken alleen hoogwaardige componenten. De garantieperiode verschilt per product en wordt duidelijk vermeld in je offerte.'
    },
    {
      question: 'Kan ik mijn auto tijdens de installatie gebruiken?',
      answer: 'Nee, tijdens de installatie hebben we je auto nodig in onze werkplaats. We raden aan om alternatief vervoer te regelen. Voor langere projecten kunnen we samen een planning maken die bij jou past.'
    },
    {
      question: 'Welke auto merken ondersteunen jullie?',
      answer: 'We werken met vrijwel alle automerken! Van Audi, BMW, Mercedes tot Volkswagen, Toyota en meer. Neem contact op met je specifieke automerk en model, dan bekijken we de mogelijkheden.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact DSF Cartech - Auto Upgrades Antwerpen",
    "description": "Neem contact op met DSF Cartech voor professionele auto-upgrades in Antwerpen. Gratis consultatie en persoonlijk advies.",
    "url": `${siteUrl}/contact`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "DSF Cartech",
      "telephone": "+32489170592",
      "email": "Dsf.cartech@hotmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Ternesselei 191",
        "addressLocality": "Wommelgem",
        "addressRegion": "Antwerpen",
        "postalCode": "2160",
        "addressCountry": "BE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "51.2194",
        "longitude": "4.4025"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "16:00"
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="Contact DSF Cartech - Auto Upgrades Antwerpen | Gratis Consultatie"
        description="Neem contact op met DSF Cartech voor professionele auto-upgrades in Antwerpen. Gratis consultatie, persoonlijk advies. Bel 0489 17 05 92 of plan een afspraak."
        keywords="contact dsfcartech, auto upgrades antwerpen, carplay installatie afspraak, gratis consultatie, ternesselei wommelgem"
        canonical={`${siteUrl}/contact`}
        schema={schema}
      />

      <div className="min-h-screen bg-black text-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
              Contact
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Klaar om uw auto te upgraden? Neem contact op voor een gratis consultatie 
              en persoonlijk advies over de beste oplossing voor uw voertuig.
            </p>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              
              {/* Contact Form */}
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6">Stuur ons een bericht</h2>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 animate-pulse">
                    <div className="flex items-start">
                      <i className="ri-check-line text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <p className="font-semibold mb-1">Bericht succesvol verzonden!</p>
                        <p className="text-sm text-green-300">Bedankt voor uw bericht! We nemen binnen 24 uur contact met u op.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                    <div className="flex items-start">
                      <i className="ri-error-warning-line text-2xl mr-3 flex-shrink-0"></i>
                      <div>
                        <p className="font-semibold mb-1">Er ging iets mis</p>
                        <p className="text-sm text-red-300">{errorMessage || 'Probeer het opnieuw of bel ons direct op 0489 17 05 92.'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Naam <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="Uw volledige naam"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        E-mail <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="uw.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Telefoon <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="+32 489 17 05 92"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                        Gewenste Service
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none transition-colors"
                      >
                        <option value="">Selecteer een service</option>
                        <option value="carplay">CarPlay & Android Auto</option>
                        <option value="starroof">Sterrenhemel / Starroof</option>
                        <option value="ambient">Sfeerverlichting</option>
                        <option value="camera">Achteruitcamera & Parkeersystemen</option>
                        <option value="custom">Maatwerk oplossing</option>
                        <option value="consultation">Gratis consultatie</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Bericht <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none transition-colors resize-none"
                      placeholder="Vertel ons over uw auto en wat u graag zou willen upgraden..."
                    ></textarea>
                    <div className="text-right text-sm text-gray-400 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-black font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg hover:shadow-cyan-500/30"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Versturen...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Bericht Versturen
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                
                {/* Quick Contact */}
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">Direct Contact</h3>
                  
                  <div className="space-y-6">
                    <a href="tel:0489170592" className="flex items-center group cursor-pointer">
                      <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-phone-line text-black text-xl"></i>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Bel ons direct</p>
                        <p className="text-white font-semibold text-lg">0489 17 05 92</p>
                      </div>
                    </a>
                    
                    <a href="https://wa.me/32489170592" target="_blank" rel="noopener noreferrer" className="flex items-center group cursor-pointer">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-whatsapp-line text-white text-xl"></i>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">WhatsApp</p>
                        <p className="text-white font-semibold text-lg">0489 17 05 92</p>
                      </div>
                    </a>
                    
                    <a href="mailto:Dsf.cartech@hotmail.com" className="flex items-center group cursor-pointer">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">E-mail</p>
                        <p className="text-white font-semibold text-lg">Dsf.cartech@hotmail.com</p>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Location Info */}
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">Bezoek Onze Werkplaats</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-cyan-400 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="text-white font-medium">DSF Cartech</p>
                        <p className="text-gray-300">Ternesselei 191</p>
                        <p className="text-gray-300">2160 Wommelgem, België</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <i className="ri-time-line text-cyan-400 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="text-white font-medium mb-2">Openingstijden</p>
                        <div className="text-gray-300 text-sm space-y-1">
                          <p>Maandag - Vrijdag: 08:00 - 18:00</p>
                          <p>Zaterdag: 09:00 - 16:00</p>
                          <p>Zondag: Gesloten</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <a
                    href="https://www.google.com/maps/dir//Ternesselei+191,+2160+Wommelgem"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-black font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center whitespace-nowrap cursor-pointer shadow-lg hover:shadow-cyan-500/30 hover:scale-105"
                  >
                    <i className="ri-navigation-line mr-2"></i>
                    Route Plannen
                  </a>
                </div>

                {/* FAQ Quick Links */}
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">Veelgestelde Vragen</h3>
                  
                  <div className="space-y-3">
                    {faqItems.map((item, index) => (
                      <div key={index} className="border border-gray-700 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                          className="w-full flex items-center justify-between text-left p-4 hover:bg-gray-800/50 transition-colors cursor-pointer"
                        >
                          <span className="text-gray-300 hover:text-cyan-400 transition-colors font-medium pr-4">
                            {item.question}
                          </span>
                          <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-cyan-400 text-xl flex-shrink-0 transition-transform duration-300`}></i>
                        </button>
                        
                        <div 
                          className={`overflow-hidden transition-all duration-300 ${
                            expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="p-4 pt-0 text-gray-400 leading-relaxed">
                            {item.answer}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/diensten')}
                    className="mt-6 w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center whitespace-nowrap cursor-pointer"
                  >
                    Bekijk Alle Diensten
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 px-4 bg-gray-900/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Bekijk op Google maps
            </h2>
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2498.8!2d4.5247!3d51.1956!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3f6f7c7c7c7c7%3A0x7c7c7c7c7c7c7c7c!2sTermesselei%20191%2C%202160%20Wommelgem!5e0!3m2!1snl!2sbe!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DSF Cartech Locatie - Ternesselei 191, Wommelgem"
              ></iframe>
              
              <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 text-white shadow-xl">
                <h4 className="font-bold mb-2">DSF Cartech</h4>
                <p className="text-sm text-gray-300">Ternesselei 191, Wommelgem</p>
                <p className="text-sm text-cyan-400">0489 17 05 92</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}